import React, { useEffect, useRef, useState } from 'react';
import './Hero3D.css';

const FRAME_COUNT = 262;
const INITIAL_FRAME_COUNT = 8;
const FRAME_PATH = '/animation/ezgif-frame-';

function frameSource(frame) {
  return `${FRAME_PATH}${String(frame).padStart(3, '0')}.webp`;
}

export default function Hero3D({ onOpenModal }) {
  const [viewportPhase, setViewportPhase] = useState('before');
  const heroRef = useRef(null);
  const imageRef = useRef(null);
  const frameRef = useRef(1);
  const phaseRef = useRef('before');

  useEffect(() => {
    const loadedFrames = new Map();
    let preloadCancelled = false;
    let nextBackgroundFrame = INITIAL_FRAME_COUNT + 1;
    let idleHandle;

    const preloadFrame = (frame) => {
      if (loadedFrames.has(frame)) return loadedFrames.get(frame);
      const image = new Image();
      image.src = frameSource(frame);
      const decodePromise = image.decode ? image.decode().catch(() => image) : Promise.resolve(image);
      loadedFrames.set(frame, decodePromise);
      return decodePromise;
    };

    for (let frame = 1; frame <= INITIAL_FRAME_COUNT; frame += 1) {
      preloadFrame(frame);
    }

    const preloadRemainingFrames = () => {
      if (preloadCancelled || nextBackgroundFrame > FRAME_COUNT) return;
      preloadFrame(nextBackgroundFrame);
      nextBackgroundFrame += 1;
      idleHandle = window.requestIdleCallback
        ? window.requestIdleCallback(preloadRemainingFrames)
        : window.setTimeout(preloadRemainingFrames, 100);
    };

    idleHandle = window.requestIdleCallback
      ? window.requestIdleCallback(preloadRemainingFrames)
      : window.setTimeout(preloadRemainingFrames, 100);

    let animationFrame = 0;
    let ticking = false;
    let lastFrameUpdate = 0;
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    const isConstrainedDevice = isMobile || window.matchMedia('(pointer: coarse)').matches
      || (navigator.deviceMemory && navigator.deviceMemory <= 4)
      || (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4);
    const frameStep = isMobile ? 3 : isConstrainedDevice ? 2 : 1;
    const frameInterval = isMobile ? 1000 / 24 : 0;

    const updateFrame = (timestamp = 0) => {
      ticking = false;
      if (frameInterval && timestamp - lastFrameUpdate < frameInterval) return;
      lastFrameUpdate = timestamp;
      const hero = heroRef.current;
      if (!hero) return;

      const scrollDistance = Math.max(1, hero.offsetHeight - window.innerHeight);
      const heroTop = hero.offsetTop;
      const scrollOffset = window.scrollY - heroTop;
      const nextPhase = scrollOffset < 0
        ? 'before'
        : scrollOffset > scrollDistance
          ? 'after'
          : 'fixed';
      if (nextPhase !== phaseRef.current) {
        phaseRef.current = nextPhase;
        setViewportPhase(nextPhase);
      }

      const progress = Math.max(0, Math.min(1, scrollOffset / scrollDistance));
      const rawFrame = Math.round(progress * (FRAME_COUNT - 1)) + 1;
      const nextFrame = rawFrame === FRAME_COUNT
        ? FRAME_COUNT
        : Math.max(1, Math.round(rawFrame / frameStep) * frameStep);
      if (nextFrame !== frameRef.current) {
        frameRef.current = nextFrame;
        if (imageRef.current) {
          const cachedFrame = loadedFrames.get(nextFrame);
          if (cachedFrame) {
            cachedFrame.then(() => {
              if (imageRef.current && frameRef.current === nextFrame) {
                imageRef.current.src = frameSource(nextFrame);
              }
            });
          } else {
            preloadFrame(nextFrame).then(() => {
              if (imageRef.current && frameRef.current === nextFrame) {
                imageRef.current.src = frameSource(nextFrame);
              }
            });
          }
        }
      }
    };

    const handleScroll = () => {
      if (!ticking) {
        ticking = true;
        animationFrame = requestAnimationFrame(updateFrame);
      }
    };

    updateFrame();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    return () => {
      preloadCancelled = true;
      if (window.cancelIdleCallback && typeof idleHandle === 'number') {
        window.cancelIdleCallback(idleHandle);
      } else {
        window.clearTimeout(idleHandle);
      }
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      cancelAnimationFrame(animationFrame);
    };
  }, []);

  return (
    <section ref={heroRef} data-hero-animation className="relative h-[300vh] w-full bg-[#05070b]">
      <div
        className={`hero3d-viewport w-full overflow-hidden ${viewportPhase === 'fixed' ? 'fixed inset-x-0 top-0 z-0' : 'absolute inset-x-0'}`}
        style={viewportPhase === 'after' ? { top: 'calc(100% - 100vh)' } : undefined}
      >
        <img
          ref={imageRef}
          src={frameSource(1)}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
          draggable={false}
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/55" />
      </div>
    </section>
  );
}
